import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiMinus, PiPlus, PiWarehouse, PiBoat } from 'react-icons/pi';
import { Button, Text } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import ReservationExpiryCountdown from '@v2/features/stock-reservations/ReservationExpiryCountdown';
import {
    StockPhaseBadge,
    StockShipmentLink,
    phaseFromSource,
} from '@v2/features/products/StockPhaseBadge';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import type { OrderProductCard } from '../types';
import { DOT_STYLE, dotInlineStyle } from '@v2/features/colors/ColorDot';

type Source = 'cd' | 'shipment';

const ANY_COLOR = '__any__';

type Props = {
    open: boolean;
    onClose: () => void;
    dealId: number;
    product: OrderProductCard;
    canRequest: boolean;
};

function isActiveReservation(status?: string | null): boolean {
    return status === 'reserved' || status === 'pending' || status === 'approved';
}

function formatExpiresAt(value?: string | null): string | null {
    if (!value) return null;
    try {
        return new Intl.DateTimeFormat('pt-BR', {
            dateStyle: 'short',
            timeStyle: 'short',
        }).format(new Date(value));
    } catch {
        return value;
    }
}

export default function StockRequestModal({ open, onClose, dealId, product, canRequest }: Props) {
    const { t } = useI18n();
    const qc = useQueryClient();
    const lineQty = Math.round(product.quantity);
    const cdAvailable = product.available_to_request ?? product.stock_available ?? 0;
    const shipmentSources = product.shipment_sources ?? [];
    const request = product.stock_request;
    const active = isActiveReservation(request?.status);

    const [source, setSource] = useState<Source>('cd');
    const [shipmentId, setShipmentId] = useState<number | null>(null);
    const [qty, setQty] = useState(1);
    const [reason, setReason] = useState('');
    // Cor a reservar: sem isso o CD separava qualquer cor do produto (reunião Supply Chain).
    const colorOptions = (product.stock_color_options ?? []).filter((c) => c.available > 0);
    const lineColorKey = product.line_color_key ?? '';
    const lineHasColor = lineColorKey.replace(/\|/g, '') !== '';
    const lineOption = lineHasColor
        ? colorOptions.find((c) => c.key.toLowerCase() === lineColorKey.toLowerCase())
        : undefined;
    // null = ainda não escolheu; ANY = qualquer cor (explícito).
    const [colorKey, setColorKey] = useState<string | null>(null);
    const selectedColor = colorOptions.find((c) => c.key === colorKey) ?? null;

    const selectedShipment = useMemo(
        () => shipmentSources.find((s) => s.shipment_id === shipmentId) ?? null,
        [shipmentSources, shipmentId],
    );

    const poolAvailable =
        source === 'cd'
            ? (selectedColor ? selectedColor.available : cdAvailable)
            : (selectedShipment?.qty_available ?? 0);
    const maxQty = Math.max(0, Math.min(lineQty, poolAvailable));

    useEffect(() => {
        if (!open) return;
        const sources = product.shipment_sources ?? [];
        const preferCd = cdAvailable > 0;
        const nextSource: Source = preferCd || sources.length === 0 ? 'cd' : 'shipment';
        setSource(nextSource);
        setShipmentId(sources[0]?.shipment_id ?? null);
        setQty(1);
        setReason('');
        setColorKey(lineOption?.key ?? (colorOptions.length === 1 ? colorOptions[0].key : colorOptions.length === 0 ? ANY_COLOR : null));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when modal opens / product changes
    }, [open, product.id, cdAvailable]);

    useEffect(() => {
        if (qty > maxQty && maxQty >= 1) setQty(maxQty);
    }, [maxQty, qty]);

    const invalidate = () => {
        void qc.invalidateQueries({ queryKey: ['ploomes-deal-products', dealId] });
    };

    const requestMut = useMutation({
        mutationFn: async () => {
            const { data } = await api.post(`/ploomes-deals/${dealId}/stock-requests`, {
                ploomes_order_product_id: product.id,
                qty,
                reason: reason.trim(),
                source,
                source_shipment_id: source === 'shipment' ? shipmentId : null,
                color_key: source === 'cd' && colorKey !== ANY_COLOR ? colorKey : null,
            });
            return data;
        },
        onSuccess: (data) => {
            toast.success(data.message ?? 'Reserva de estoque criada.');
            invalidate();
            onClose();
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.message ?? 'Falha ao reservar estoque');
        },
    });

    const cancelMut = useMutation({
        mutationFn: async () => {
            if (!request?.id) return;
            const { data } = await api.post(`/stock-requests/${request.id}/cancel`);
            return data;
        },
        onSuccess: (data) => {
            toast.success(data?.message ?? 'Reserva cancelada');
            invalidate();
            onClose();
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.message ?? 'Falha ao cancelar');
        },
    });

    const clamp = (n: number) => Math.min(maxQty, Math.max(1, n));
    const fromPool = Math.min(qty, maxQty);
    const toImport = Math.max(0, lineQty - fromPool);
    const expiresLabel = formatExpiresAt(request?.expires_at);

    const title = active ? 'Estoque reservado' : 'Reservar estoque';

    const canSubmit =
        canRequest &&
        !active &&
        maxQty >= 1 &&
        qty >= 1 &&
        qty <= maxQty &&
        reason.trim().length >= 3 &&
        (source !== 'cd' || colorKey !== null) &&
        (source !== 'shipment' || Boolean(shipmentId));

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            title={title}
            description={product.product_name}
            size={active ? 'md' : '2xl'}
            footer={
                active ? (
                    <>
                        <Button variant="outline" onClick={onClose}>
                            Fechar
                        </Button>
                        {canRequest && (
                            <Button
                                className="bg-rose-600 text-white hover:bg-rose-700"
                                isLoading={cancelMut.isPending}
                                onClick={() => cancelMut.mutate()}
                            >
                                Cancelar reserva
                            </Button>
                        )}
                    </>
                ) : (
                    <>
                        <Button variant="outline" onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button
                            disabled={!canSubmit}
                            isLoading={requestMut.isPending}
                            onClick={() => requestMut.mutate()}
                        >
                            <PiWarehouse className="me-1.5 size-4" />
                            Reservar estoque
                        </Button>
                    </>
                )
            }
        >
            <div className="space-y-4">
                <ProductStrip product={product} lineQty={lineQty} />

                {active && (
                    <>
                        <StatusBanner tone="success">
                            <span className="flex flex-wrap items-center gap-2">
                                <span>
                                    Reserva ativa: <strong>{request?.qty_requested}</strong> un.
                                </span>
                                <StockPhaseBadge
                                    phase={request?.stock_phase ?? phaseFromSource(request?.source)}
                                    label={request?.source_label}
                                />
                                {request?.source === 'shipment' && (
                                    <StockShipmentLink
                                        shipmentId={request.source_shipment?.id ?? request.source_shipment_id}
                                        label={request.source_shipment?.label}
                                    />
                                )}
                            </span>
                            {expiresLabel ? <span className="mt-1 block text-xs opacity-80">Expira em {expiresLabel}</span> : null}
                            {request?.reason ? <span className="mt-1 block text-xs opacity-80">Motivo: {request.reason}</span> : null}
                        </StatusBanner>
                        <ReservationExpiryCountdown
                            reservedAt={request?.reserved_at ?? request?.requested_at}
                            expiresAt={request?.expires_at}
                            active
                            size="md"
                        />
                    </>
                )}

                {!active && cdAvailable < 1 && shipmentSources.length === 0 && (
                    <Text className="text-sm text-gray-500">Não há estoque no CD nem em processo aberto para reservar.</Text>
                )}

                {!active && (cdAvailable > 0 || shipmentSources.length > 0) && (
                    <>
                        {product.is_high_turnover && (
                            <p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-800 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
                                Produto de alto giro: reservar trava estoque da venda pronta-entrega. Valide com o comercial antes de reservar.
                            </p>
                        )}

                        <div className="grid gap-5 md:grid-cols-2">
                            {/* Coluna 1: origem + cor/processo */}
                            <div className="min-w-0 space-y-4">
                                <div>
                                    <StepTitle n={1}>Origem</StepTitle>
                                    <div className="grid grid-cols-2 gap-2">
                                        <SourceChip
                                            active={source === 'cd'}
                                            disabled={cdAvailable < 1}
                                            icon={<PiWarehouse className="size-5" />}
                                            label={t('v2.stock_phase.terra')}
                                            count={cdAvailable}
                                            onClick={() => {
                                                setSource('cd');
                                                setQty(1);
                                            }}
                                        />
                                        <SourceChip
                                            active={source === 'shipment'}
                                            disabled={shipmentSources.length === 0}
                                            icon={<PiBoat className="size-5" />}
                                            label={t('v2.stock_phase.transito')}
                                            count={product.shipment_available_total ?? 0}
                                            onClick={() => {
                                                setSource('shipment');
                                                setShipmentId(shipmentSources[0]?.shipment_id ?? null);
                                                setQty(1);
                                            }}
                                        />
                                    </div>
                                </div>

                                {source === 'cd' && colorOptions.length > 0 && (
                                    <div>
                                        <StepTitle n={2}>Cor a reservar</StepTitle>
                                        <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
                                            {colorOptions.map((c) => (
                                                <button
                                                    key={c.key}
                                                    type="button"
                                                    onClick={() => {
                                                        setColorKey(c.key);
                                                        setQty(1);
                                                    }}
                                                    className={cn(
                                                        'flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs transition',
                                                        colorKey === c.key ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-gray-200 hover:border-gray-300 dark:border-zinc-700',
                                                    )}
                                                >
                                                    <span className="flex -space-x-1">
                                                        {c.swatches.map((sw) => (
                                                            <span
                                                                key={sw.category}
                                                                className={cn('size-4 rounded-full border-2 border-white dark:border-zinc-900', !sw.hex && DOT_STYLE.off)}
                                                                style={dotInlineStyle(sw.hex ? 'standard' : 'off', sw.hex)}
                                                            />
                                                        ))}
                                                    </span>
                                                    <span className="min-w-0 flex-1 truncate text-gray-800 dark:text-zinc-100">{c.label}</span>
                                                    {lineOption?.key === c.key && (
                                                        <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">cor vendida</span>
                                                    )}
                                                    <span className="font-semibold tabular-nums text-emerald-700">{c.available}</span>
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setColorKey(ANY_COLOR);
                                                    setQty(1);
                                                }}
                                                className={cn(
                                                    'w-full rounded-lg border border-dashed px-3 py-1.5 text-left text-xs transition',
                                                    colorKey === ANY_COLOR ? 'border-primary bg-primary/5 text-gray-800' : 'border-gray-300 text-gray-500 hover:border-gray-400',
                                                )}
                                            >
                                                Qualquer cor ({cdAvailable} un.)
                                            </button>
                                        </div>
                                        {lineHasColor && !lineOption && (
                                            <p className="mt-1.5 text-[11px] text-amber-700">Não há saldo no CD na cor vendida — confirme com o cliente antes de reservar outra cor.</p>
                                        )}
                                        {lineOption && colorKey !== null && colorKey !== lineOption.key && (
                                            <p className="mt-1.5 text-[11px] text-amber-700">Cor diferente da vendida no pedido — confirme com o cliente.</p>
                                        )}
                                    </div>
                                )}

                                {source === 'shipment' && (
                                    <div>
                                        <StepTitle n={2}>Processo de origem</StepTitle>
                                        <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
                                            {shipmentSources.map((s) => (
                                                <button
                                                    key={s.shipment_id}
                                                    type="button"
                                                    onClick={() => {
                                                        setShipmentId(s.shipment_id);
                                                        setQty(1);
                                                    }}
                                                    className={cn(
                                                        'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition',
                                                        shipmentId === s.shipment_id ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-gray-200 hover:border-gray-300 dark:border-zinc-700',
                                                    )}
                                                >
                                                    <span
                                                        className={cn(
                                                            'flex size-4 shrink-0 items-center justify-center rounded-full border-2',
                                                            shipmentId === s.shipment_id ? 'border-primary' : 'border-gray-300 dark:border-zinc-600',
                                                        )}
                                                    >
                                                        {shipmentId === s.shipment_id && <span className="size-2 rounded-full bg-primary" />}
                                                    </span>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="flex items-center gap-2 text-sm">
                                                            <span className="font-mono font-bold text-orange-600">{s.label}</span>
                                                            <span className="truncate text-xs text-gray-500">{s.stage_label}</span>
                                                        </p>
                                                        <p className="truncate text-xs text-gray-500" title={[s.client, s.deal_title].filter(Boolean).join(' · ')}>
                                                            {[s.client, s.deal_title].filter(Boolean).join(' · ') || '—'}
                                                        </p>
                                                        {s.eta_label && <p className="text-[11px] font-medium text-sky-700 dark:text-sky-300">ETA CD: {s.eta_label}</p>}
                                                    </div>
                                                    <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold tabular-nums text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                                                        {s.qty_available} un.
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                        {selectedShipment && (
                                            <div className="mt-1.5 text-right">
                                                <StockShipmentLink shipmentId={selectedShipment.shipment_id} label={`Abrir processo ${selectedShipment.label}`} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Coluna 2: quantidade + motivo + resultado */}
                            <div className="min-w-0 space-y-4">
                                {maxQty < 1 ? (
                                    <Text className="text-sm text-gray-500">Selecione uma origem com disponibilidade.</Text>
                                ) : (
                                    <>
                                        <div>
                                            <StepTitle n={3}>Quantidade</StepTitle>
                                            <div className="flex items-center gap-2">
                                                <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-zinc-600">
                                                    <button
                                                        type="button"
                                                        className="flex size-9 items-center justify-center text-gray-500 transition hover:bg-gray-50 disabled:opacity-40 dark:hover:bg-zinc-800"
                                                        disabled={qty <= 1}
                                                        onClick={() => setQty((q) => clamp(q - 1))}
                                                        aria-label="Diminuir"
                                                    >
                                                        <PiMinus className="size-4" />
                                                    </button>
                                                    <span className="min-w-[2.5rem] text-center text-base font-semibold tabular-nums text-gray-900 dark:text-zinc-100">{qty}</span>
                                                    <button
                                                        type="button"
                                                        className="flex size-9 items-center justify-center text-gray-500 transition hover:bg-gray-50 disabled:opacity-40 dark:hover:bg-zinc-800"
                                                        disabled={qty >= maxQty}
                                                        onClick={() => setQty((q) => clamp(q + 1))}
                                                        aria-label="Aumentar"
                                                    >
                                                        <PiPlus className="size-4" />
                                                    </button>
                                                </div>
                                                <button
                                                    type="button"
                                                    disabled={qty >= maxQty}
                                                    onClick={() => setQty(maxQty)}
                                                    className="rounded-lg border border-gray-200 px-2.5 py-2 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-40 dark:border-zinc-600 dark:text-zinc-300 dark:hover:bg-zinc-800"
                                                >
                                                    Máx. {maxQty}
                                                </button>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="mb-1.5 block text-sm font-semibold text-gray-800 dark:text-zinc-100">
                                                Motivo da reserva <span className="text-rose-500">*</span>
                                            </label>
                                            <textarea
                                                className="min-h-[76px] w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
                                                value={reason}
                                                onChange={(e) => setReason(e.target.value)}
                                                placeholder="Ex.: cliente precisa de pronta entrega até sexta"
                                                required
                                                minLength={3}
                                            />
                                        </div>

                                        <SplitBar
                                            fromPool={fromPool}
                                            toImport={toImport}
                                            poolLabel={source === 'shipment' ? t('v2.stock_phase.transito') : t('v2.stock_phase.terra')}
                                        />

                                        <p className="text-xs leading-relaxed text-gray-400">
                                            A reserva é imediata e segura a unidade até a data de expiração.
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </V2Modal>
    );
}

function SourceChip({
    active,
    disabled,
    icon,
    label,
    count,
    onClick,
}: {
    active: boolean;
    disabled?: boolean;
    icon: React.ReactNode;
    label: string;
    count: number;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className={cn(
                'flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition',
                active ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-gray-200 hover:border-gray-300 dark:border-zinc-700',
                disabled && 'cursor-not-allowed opacity-40',
            )}
        >
            <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', active ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500 dark:bg-zinc-800')}>
                {icon}
            </span>
            <span className="min-w-0">
                <span className="block text-sm font-semibold text-gray-900 dark:text-zinc-100">{label}</span>
                <span className={cn('block text-xs tabular-nums', count > 0 ? 'font-semibold text-emerald-700 dark:text-emerald-300' : 'text-gray-500')}>
                    {count} un.
                </span>
            </span>
        </button>
    );
}

function StepTitle({ n, children }: { n: number; children: React.ReactNode }) {
    return (
        <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-zinc-100">
            <span className="flex size-5 items-center justify-center rounded-full bg-gray-900 text-[11px] font-bold text-white dark:bg-zinc-100 dark:text-zinc-900">{n}</span>
            {children}
        </p>
    );
}

/** Cabeçalho do produto: foto, código, quantidade do pedido e cores vendidas. */
function ProductStrip({ product, lineQty }: { product: OrderProductCard; lineQty: number }) {
    const swatches = product.color_swatches.filter((sw) => sw.name);

    return (
        <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/70 p-2.5 dark:border-zinc-800 dark:bg-zinc-800/40">
            {product.image_url ? (
                <img src={product.image_url} alt="" className="size-14 shrink-0 rounded-lg bg-white object-contain p-1" />
            ) : (
                <span className="flex size-14 shrink-0 items-center justify-center rounded-lg bg-white text-gray-300 dark:bg-zinc-900">
                    <PiWarehouse className="size-6" />
                </span>
            )}
            <div className="min-w-0 flex-1">
                {product.product_code && <p className="font-mono text-xs text-gray-400">{product.product_code}</p>}
                <p className="text-sm text-gray-600 dark:text-zinc-300">
                    Pedido: <span className="font-semibold text-gray-900 dark:text-zinc-100">{lineQty}</span> un.
                </p>
            </div>
            {swatches.length > 0 && (
                <span className="flex -space-x-1" title={swatches.map((sw) => `${sw.label}: ${sw.name}`).join('\n')}>
                    {swatches.map((sw) => (
                        <span
                            key={sw.slot}
                            className={cn('size-5 rounded-full border-2 border-white dark:border-zinc-900', !sw.hex && DOT_STYLE.off)}
                            style={dotInlineStyle(sw.hex ? 'standard' : 'off', sw.hex)}
                        />
                    ))}
                </span>
            )}
        </div>
    );
}

/** Resultado da reserva: quanto sai da origem escolhida e quanto ainda vai para importação. */
function SplitBar({ fromPool, toImport, poolLabel }: { fromPool: number; toImport: number; poolLabel: string }) {
    const total = Math.max(1, fromPool + toImport);

    return (
        <div className="rounded-xl border border-gray-100 p-3 dark:border-zinc-800">
            <div className="flex h-2 overflow-hidden rounded-full bg-sky-100 dark:bg-sky-950/50">
                <div className="bg-emerald-500 transition-all" style={{ width: `${(fromPool / total) * 100}%` }} />
            </div>
            <div className="mt-2 flex justify-between text-xs">
                <span className="text-emerald-700 dark:text-emerald-300">
                    <span className="font-bold tabular-nums">{fromPool}</span> {poolLabel.toLowerCase()}
                </span>
                <span className="text-sky-700 dark:text-sky-300">
                    <span className="font-bold tabular-nums">{toImport}</span> a importar
                </span>
            </div>
        </div>
    );
}

function StatusBanner({
    tone,
    children,
}: {
    tone: 'warning' | 'success';
    children: React.ReactNode;
}) {
    return (
        <div
            className={cn(
                'rounded-xl px-3 py-2.5 text-sm',
                tone === 'warning' &&
                    'bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200',
                tone === 'success' &&
                    'bg-emerald-50 text-emerald-900 dark:bg-emerald-500/10 dark:text-emerald-200',
            )}
        >
            {children}
        </div>
    );
}
